import { useEffect, useRef } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";

type UseDebouncedAutosaveOptions<TFieldValues extends FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  onSave: (values: TFieldValues) => Promise<void>;
  delay?: number;
  enabled?: boolean;
};

export function useDebouncedAutosave<TFieldValues extends FieldValues>({
  form,
  onSave,
  delay = 1000,
  enabled = true,
}: UseDebouncedAutosaveOptions<TFieldValues>) {
  const watchedValues = useWatch({ control: form.control });
  const timeoutRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string>("");

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    if (
      !form.formState.isDirty ||
      !form.formState.isValid ||
      form.formState.isSubmitting
    ) {
      return;
    }

    timeoutRef.current = window.setTimeout(async () => {
      if (inFlightRef.current) {
        return;
      }

      const values = form.getValues();
      const snapshot = JSON.stringify(values);

      if (snapshot === lastSavedSnapshotRef.current) {
        return;
      }

      inFlightRef.current = true;

      try {
        await onSave(values);
        lastSavedSnapshotRef.current = snapshot;
      } finally {
        inFlightRef.current = false;
      }
    }, delay);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [
    watchedValues,
    enabled,
    delay,
    form,
    form.formState.isDirty,
    form.formState.isValid,
    form.formState.isSubmitting,
    onSave,
  ]);
}
